// web-frontend/src/shared/services/notificationsService.js - 웹용
/**
 * 🔥 웹용 알림 서비스
 * - expo-notifications → Web Push API 필요
 * - 실제 API 호출 + 더미 구현 fallback 제공
 * 
 * 🔧 [수정사항]
 * 1. fetchNotifications → getNotifications 함수명 변경 (Store와 일치)
 * 2. 실제 백엔드 API 호출 구현
 * 3. markAsRead API 호출 구현
 * 4. markAllAsRead 함수 추가 (누락된 함수)
 * 5. 🔥 API 호출 템플릿 리터럴 문법 오류 수정
 * 6. deleteNotification 메서드 추가 (스와이프 삭제용)
 * 7. 🔥🔥🔥 [핵심] 백엔드 배열 응답 처리 + unreadCount 직접 계산
 * 8. 🔥 응답 데이터 구조 정규화 로직 개선
 * 9. 🔥🔥🔥 [2026-01-16] 템플릿 리터럴 문법 오류 전면 수정
 * 10. 🔥🔥🔥 [2026-01-20] 템플릿 리터럴 + 함수 호출 괄호 문법 완전 수정
 */
import apiClient from './apiClient';
import logger from '../utils/logger';

class NotificationsService {
  constructor() {
    this.initialized = false;
    this.hasPermission = false;
    logger.debug('🔔 Notifications Service (웹용)');
  }

  /**
   * 초기화
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    logger.debug('🔔 알림 서비스 초기화 (웹 환경)');
    
    // 브라우저 알림 지원 확인
    if ('Notification' in window) {
      logger.debug('✅ 브라우저가 Web Notifications를 지원합니다');
      // TODO: Web Push API 구현
    } else {
      logger.warn('⚠️ 브라우저가 Web Notifications를 지원하지 않습니다');
    }

    this.initialized = true;
  }

  /**
   * 푸시 알림 초기화 (웹에서는 비활성화)
   */
  async initializePushNotifications() {
    logger.debug('📱 푸시 알림 초기화 (웹 환경 - 비활성화)');
    // 웹에서는 Service Worker + Web Push API 필요
    return false;
  }

  /**
   * 알림 권한 요청
   */
  async requestPermission() {
    try {
      if (!('Notification' in window)) {
        logger.warn('⚠️ 브라우저가 알림을 지원하지 않습니다');
        return false;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        logger.debug('✅ 알림 권한 허용됨');
        this.hasPermission = true;
        return true;
      } else if (permission === 'denied') {
        logger.warn('⚠️ 알림 권한 거부됨');
        this.hasPermission = false;
        return false;
      } else {
        logger.debug('ℹ️ 알림 권한 대기 중');
        this.hasPermission = false;
        return false;
      }
    } catch (error) {
      logger.error('❌ 알림 권한 요청 실패:', error);
      return false;
    }
  }

  /**
   * 권한 상태 확인
   */
  async checkPermissionStatus() {
    try {
      if (!('Notification' in window)) {
        return { hasPermission: false, status: 'unsupported' };
      }

      const permission = Notification.permission;
      return {
        hasPermission: permission === 'granted',
        status: permission,
      };
    } catch (error) {
      logger.error('권한 상태 확인 실패:', error);
      return { hasPermission: false, status: 'error' };
    }
  }

  /**
   * 로컬 알림 보내기 (브라우저 알림)
   */
  async sendLocalNotification(title, body, data = {}) {
    try {
      if (!('Notification' in window)) {
        logger.warn('⚠️ 브라우저가 알림을 지원하지 않습니다');
        return false;
      }

      if (Notification.permission !== 'granted') {
        logger.warn('⚠️ 알림 권한이 없습니다');
        return false;
      }

      const notification = new Notification(title, {
        body,
        icon: '/icon.png', // 앱 아이콘 경로
        badge: '/badge.png',
        data,
        requireInteraction: false,
      });

      notification.onclick = () => {
        logger.debug('알림 클릭:', data);
        window.focus();
        notification.close();
        // TODO: 데이터에 따라 페이지 이동
        // if (data.url) {
        //   window.location.href = data.url;
        // }
      };

      logger.debug('✅ 알림 전송 완료:', title);
      return true;
    } catch (error) {
      logger.error('❌ 알림 전송 실패:', error);
      return false;
    }
  }

  /**
   * 알림 예약 (웹에서는 제한적)
   */
  async scheduleNotification(title, body, triggerDate, data = {}) {
    logger.warn('⚠️ 웹 환경에서는 알림 예약이 제한적입니다');
    logger.debug('예약 요청:', { title, body, triggerDate, data });
    // TODO: Service Worker를 사용한 알림 예약 구현
    // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration/showNotification
    return false;
  }

  /**
   * 예약된 알림 취소
   */
  async cancelScheduledNotification(notificationId) {
    logger.debug('알림 취소 요청:', notificationId);
    // 웹에서는 Service Worker로 관리 필요
    return false;
  }

  /**
   * 모든 알림 취소
   */
  async cancelAllNotifications() {
    logger.debug('모든 알림 취소 (웹 환경 - 불필요)');
    return true;
  }

  /**
   * 알림 리스너 추가 (더미)
   */
  addNotificationReceivedListener(callback) {
    logger.debug('알림 수신 리스너 추가 (웹용 - 더미)');
    return { remove: () => {} };
  }

  /**
   * 알림 응답 리스너 추가 (더미)
   */
  addNotificationResponseReceivedListener(callback) {
    logger.debug('알림 응답 리스너 추가 (웹용 - 더미)');
    return { remove: () => {} };
  }

  /**
   * 배지 카운트 설정 (웹에서는 제한적)
   * 🔥🔥🔥 [2026-01-20] 템플릿 리터럴 문법 오류 수정 - logger.debug() 괄호 추가
   */
  async setBadgeCount(count) {
    try {
      // Badge API 지원 확인
      if ('setAppBadge' in navigator) {
        await navigator.setAppBadge(count);
        // 🔥🔥🔥 [수정] console.log`...` → logger.debug(`...`)
        logger.debug(`✅ 배지 카운트 설정: ${count}`);
        return true;
      } else {
        logger.warn('⚠️ 브라우저가 Badge API를 지원하지 않습니다');
        return false;
      }
    } catch (error) {
      logger.error('배지 카운트 설정 실패:', error);
      return false;
    }
  }

  /**
   * 배지 카운트 제거
   */
  async clearBadgeCount() {
    try {
      if ('clearAppBadge' in navigator) {
        await navigator.clearAppBadge();
        logger.debug('✅ 배지 카운트 제거');
        return true;
      }
      return false;
    } catch (error) {
      logger.error('배지 카운트 제거 실패:', error);
      return false;
    }
  }

  /**
   * 알림 설정 가져오기 (더미)
   */
  async fetchSettings() {
    return {
      enabled: false,
      meetingReminders: false,
      chatMessages: false,
      carbonUpdates: false,
    };
  }

  /**
   * 알림 설정 업데이트 (더미)
   */
  async updateSettings(settings) {
    logger.debug('알림 설정 업데이트 (웹용 - 더미):', settings);
    return true;
  }

  /**
   * 🔥🔥🔥 [핵심 수정] 알림 목록 가져오기
   * - 함수명: getNotifications (Store와 일치)
   * - 실제 백엔드 API 호출 구현
   * - 🔥🔥🔥 [2026-01-20] 템플릿 리터럴 문법 오류 수정 - apiClient.get() 괄호 추가
   * - 백엔드가 배열을 직접 반환하는 경우 처리
   * - unreadCount를 배열에서 직접 계산
   * - 🔥 응답 데이터 구조 정규화 로직 개선
   * - API 실패 시 빈 데이터 반환하여 무한 재시도 및 크래시 방지
   */
  async getNotifications({ page = 1, limit = 20 } = {}) {
    try {
      const skip = (page - 1) * limit;
      
      // 🔥🔥🔥 [수정] apiClient.get`...` → apiClient.get(`...`)
      const response = await apiClient.get(`/notifications/?skip=${skip}&limit=${limit}`);
      
      // 🔥 [수정] 응답 데이터 구조 정규화
      let data = [];
      let unreadCount = 0;
      let hasNext = false;

      if (Array.isArray(response)) {
        // 1. 응답이 바로 배열인 경우 (현재 백엔드 로직)
        data = response;
        // 배열 내에서 읽지 않은 항목 직접 계산
        unreadCount = data.filter(n => n.is_read === false).length;
        // 다음 페이지 존재 여부는 가져온 개수가 limit과 같은지로 추정
        hasNext = data.length === limit;
      } else if (response && Array.isArray(response.data)) {
        // 2. 응답이 { data: [], ... } 형태인 경우 (혹시 모를 변경 대비)
        data = response.data;
        unreadCount = response.unread_count ?? data.filter(n => !n.is_read).length;
        hasNext = response.has_next || false;
      } else if (response && response.notifications) {
        // 3. 혹시 모를 다른 응답 구조 대응
        data = response.notifications;
        unreadCount = response.unread_count || 0;
      }

      // 🔥🔥🔥 [수정] console.log`...` → logger.debug(`...`)
      logger.debug(`✅ 알림 로드 완료: ${data.length}개 (안읽음: ${unreadCount})`);
      
      return {
        data,
        unreadCount,
        hasNext,
      };
    } catch (error) {
      logger.warn('⚠️ 알림 조회 실패:', error.message);
      
      // 🔥 에러 발생 시 빈 데이터 반환하여 무한 재시도 및 크래시 방지
      return {
        data: [],
        unreadCount: 0,
        hasNext: false,
      };
    }
  }

  /**
   * 🔥🔥🔥 [핵심 수정] 알림 읽음 처리
   * - 실제 백엔드 API 호출 구현
   * - 🔥🔥🔥 [2026-01-20] 템플릿 리터럴 문법 오류 수정 - apiClient.put() 괄호 추가
   * - API 실패 시에도 UI 업데이트 진행
   */
  async markAsRead(notificationId) {
    try {
      // 🔥🔥🔥 [수정] apiClient.put`...` → apiClient.put(`...`, data)
      const response = await apiClient.put(`/notifications/${notificationId}`, { 
        is_read: true 
      });
      
      logger.debug('✅ 알림 읽음 처리 완료:', notificationId);
      return response;
    } catch (error) {
      logger.error('⚠️ 알림 읽음 처리 실패:', error.message);
      
      // 에러 무시하고 UI 업데이트 진행
      return true;
    }
  }

  /**
   * 🔥🔥🔥 [필수 추가] 모든 알림 읽음 처리
   * - 백엔드 API: POST /notifications/mark-all-read
   * - Store에서 호출하는 함수 (이 함수가 없으면 TypeError 발생)
   * - 모달의 '모두 읽음' 버튼 클릭 시 사용
   */
  async markAllAsRead() {
    try {
      // 🔥 백엔드 API 호출
      const response = await apiClient.post('/notifications/mark-all-read');
      
      logger.debug('✅ 전체 알림 읽음 처리 완료:', response);
      return response;
    } catch (error) {
      logger.error('❌ 전체 알림 읽음 처리 실패:', error);
      
      // 에러가 나더라도 UI가 멈추지 않도록 fallback 처리
      return { success: false };
    }
  }

  /**
   * 🔥🔥🔥 [신규 추가] 알림 삭제
   * - 백엔드 API: DELETE /notifications/{notificationId}
   * - 🔥🔥🔥 [2026-01-20] 템플릿 리터럴 문법 오류 수정 - apiClient.delete() 괄호 추가
   * - Store의 deleteNotification에서 호출
   * - 스와이프 삭제 기능에 사용
   */
  async deleteNotification(notificationId) {
    try {
      // 🔥🔥🔥 [수정] apiClient.delete`...` → apiClient.delete(`...`)
      const response = await apiClient.delete(`/notifications/${notificationId}`);
      
      logger.debug('✅ 알림 삭제 완료:', notificationId);
      return response;
    } catch (error) {
      logger.error('❌ 알림 삭제 실패:', error);
      throw error; // Store에서 에러 처리를 위해 throw
    }
  }

  /**
   * 미팅 알림 예약 (더미)
   */
  async scheduleMeetingReminders(meetingId, meetingTitle, meetingTime) {
    logger.warn('⚠️ 미팅 알림 예약은 웹에서 지원되지 않습니다');
    logger.debug('미팅 정보:', { meetingId, meetingTitle, meetingTime });
    return false;
  }

  /**
   * 미팅 알림 취소 (더미)
   */
  async cancelMeetingReminders(meetingId) {
    logger.debug('미팅 알림 취소 (웹용 - 더미):', meetingId);
    return true;
  }

  /**
   * 상태 확인 (디버그용)
   */
  async checkStatus() {
    const permission = await this.checkPermissionStatus();
    const badgeSupported = 'setAppBadge' in navigator;
    const notificationSupported = 'Notification' in window;

    return {
      initialized: this.initialized,
      hasPermission: permission.hasPermission,
      permissionStatus: permission.status,
      notificationSupported,
      badgeSupported,
      platform: 'web',
    };
  }

  /**
   * 서비스 정보
   */
  getServiceInfo() {
    return {
      platform: 'web',
      initialized: this.initialized,
      hasPermission: this.hasPermission,
      note: '웹 환경에서는 알림 기능이 제한적입니다',
      webPushRequired: 'Service Worker + Web Push API 구현 필요',
      apiIntegration: 'getNotifications, markAsRead, markAllAsRead, deleteNotification API 연동 완료 ✅',
      lastUpdated: '2026-01-20',
      functions: {
        getNotifications: '알림 목록 조회 (페이징 지원) + 백엔드 배열 응답 처리 + unreadCount 직접 계산 + 템플릿 리터럴 수정 ✅',
        markAsRead: '개별 알림 읽음 처리 + 템플릿 리터럴 수정 ✅',
        markAllAsRead: '전체 알림 읽음 처리 (모달 "모두 읽음" 버튼용)',
        deleteNotification: '개별 알림 삭제 (스와이프 삭제용) + 템플릿 리터럴 수정 ✅',
      }
    };
  }
}

// 싱글톤 인스턴스
const notificationsService = new NotificationsService();

// 개발 모드에서 서비스 정보 출력
if (import.meta.env.DEV) {
  setTimeout(() => {
    logger.debug('🔔 Notifications Service Info:', notificationsService.getServiceInfo());
  }, 1000);
}

export default notificationsService;